import { computeSectionTbdState } from '../jobSectionState';
import { jobSectionMap as sectionMap } from '../jobSectionMap';
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { portalApi } from "../api/portalApi";
import { setMultipleJobDetails, selectJobDetails } from "./jobDetailsSlice";
import { updateHasValues } from "./hasValuesSlice";
import { setAllApiStatus } from "./apiStatusSlice";
import { setAllTbdForms, setFullJobTbdAuditItems } from "./tbdSlice";
import { readFullJobTbdAudit } from '../fullJobTbdAudit';

const fullJobArgs = (state, jobId) => ({
  jobId,
  url: state.apiUrls?.urls?.fullJob || '/full-job',
  accountId: state.user?.userDetails?.localAccountId || state.user?.userDetails?.username || '',
  session: state.criticalData?.session || 0,
});


const normalizeKey = (value) => String(value || "")
  .toLowerCase()
  .replace(/[^a-z0-9]/g, "");



const buildTbdFormsFromFullJob = (coreData) => {
  const forms = {};

  Object.values(sectionMap).forEach((sliceKey) => {
    forms[sliceKey] = { tbdFields: {}, hasTbd: false };
  });

  const tableToSliceKey = Object.entries(sectionMap).reduce((acc, [apiKey, sliceKey]) => {
    acc[normalizeKey(apiKey)] = sliceKey;
    return acc;
  }, {});

  const tbdAudit = readFullJobTbdAudit(coreData);

  if (tbdAudit.length > 0) {
    tbdAudit.forEach((item) => {
      if (!item || typeof item !== "object") {
        return;
      }

      const tableName = item.Table;
      const columnName = item.ColumnName;

      if (!tableName || !columnName) {
        return;
      }

      const sliceKey = tableToSliceKey[normalizeKey(tableName)];
      if (!sliceKey) {
        return;
      }

      const existing = forms[sliceKey] || { tbdFields: {}, hasTbd: false };
      const zoneKey = item.Zone !== undefined && item.Zone !== null ? String(item.Zone) : null;

      if (zoneKey) {
        const zoneFields = existing.tbdFields[zoneKey] || {};
        zoneFields[columnName] = true;
        existing.tbdFields[zoneKey] = zoneFields;
      } else {
        existing.tbdFields[columnName] = true;
      }

      existing.hasTbd = true;
      forms[sliceKey] = existing;
    });

    return forms;
  }

  Object.entries(sectionMap).forEach(([apiKey, sliceKey]) => {
    const sectionData = coreData?.[apiKey];
    const records = Array.isArray(sectionData)
      ? sectionData
      : sectionData
      ? [sectionData]
      : [];

    forms[sliceKey] = computeSectionTbdState(records);
  });

  return forms;
};

const getTbdAuditItemsFromFullJob = readFullJobTbdAudit;

export const fetchFullJob = createAsyncThunk(
  "fullJob/fetch",
  async ({ jobId }, { getState, dispatch, rejectWithValue }) => {
    try {
      // Set all known sections to 'pending' at start using batch update
      const pendingStatuses = {};
      Object.values(sectionMap).forEach(sliceKey => {
        pendingStatuses[sliceKey] = 'pending';
      });
      dispatch(setAllApiStatus({ statuses: pendingStatuses }));
      
      console.log("fetchFullJob: Fetching all job data for jobId:", jobId);

      // Use RTK Query to fetch the full job data
      const resultAction = await dispatch(
        portalApi.endpoints.getFullJob.initiate(fullJobArgs(getState(), jobId), { forceRefetch: true, subscribe: false })
      );

      if (resultAction.error) {
        throw new Error(resultAction.error.data || resultAction.error.message || 'Failed to fetch full job');
      }

      const payload = resultAction.data;
      const core = payload?.[jobId] || payload?.[String(jobId)] || payload || {};
      dispatch(setFullJobTbdAuditItems(getTbdAuditItemsFromFullJob(core)));
      dispatch(setAllTbdForms(buildTbdFormsFromFullJob(core)));
      
      console.log("fetchFullJob: jobId type:", typeof jobId, "value:", jobId);
      console.log("fetchFullJob: payload keys:", Object.keys(payload));
      console.log("fetchFullJob: core keys:", Object.keys(core));
      console.log("fetchFullJob: sectionMap values:", Object.values(sectionMap));

      let sectionsProcessed = 0;
      let sectionsWithData = 0;
      
      const successStatuses = {};

      Object.entries(sectionMap).forEach(([apiKey, sliceKey]) => {
        const data = core[apiKey];
        if (!data) {
          successStatuses[sliceKey] = 'success';
          return;
        }
        const updates = Array.isArray(data) ? data : [data];
        if (updates.length === 0) {
          successStatuses[sliceKey] = 'success';
          return;
        }
        sectionsProcessed++;
        sectionsWithData++;
        
        if (sliceKey === 'changeOrderParent' || sliceKey === 'changeOrderDetails') {
          console.log(`fetchFullJob: Processing ${sliceKey} - ${updates.length} items`, updates);
        }
        
        dispatch(setMultipleJobDetails({ section: sliceKey, updates }));
        dispatch(updateHasValues(updates));
        successStatuses[sliceKey] = 'success';
      });
      
      console.log("fetchFullJob: Setting success statuses for sections:", Object.keys(successStatuses));
      dispatch(setAllApiStatus({ statuses: successStatuses }));

      console.log(`fetchFullJob: Successfully processed ${sectionsWithData} sections with data out of ${sectionsProcessed} sections`);
      return { jobId, sectionsProcessed, sectionsWithData, freshSince: resultAction.startedTimeStamp || 0 };
    } catch (err) {
      const message = err?.response?.status ? `${err.response.status}` : err?.message || "failed";
      console.error("fetchFullJob failed:", message, err);
      
      const failedStatuses = {};
      Object.values(sectionMap).forEach(sliceKey => {
        failedStatuses[sliceKey] = 'failed';
      });
      dispatch(setAllApiStatus({ statuses: failedStatuses }));
      
      return rejectWithValue(message);
    }
  }
);

/**
 * Syncs job data from API with Redux store
 * Only updates fields that have changed to preserve local state
 */
export const syncJobData = createAsyncThunk(
  "fullJob/sync",
  async ({ jobId, reuseRecent = false }, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const currentJobDetails = selectJobDetails(state);

      // Keep existing section statuses during background sync so the UI does not
      // regress back into initial loading indicators while data is already usable.
      console.log("syncJobData: Fetching latest data for jobId:", jobId);

      // Use RTK Query to fetch fresh data
      const resultAction = await dispatch(
        portalApi.endpoints.getFullJob.initiate(fullJobArgs(getState(), jobId), { forceRefetch: reuseRecent ? 5 : true, subscribe: false })
      );

      if (resultAction.error) {
        throw new Error(resultAction.error.data || resultAction.error.message || 'Failed to sync job data');
      }

      const payload = resultAction.data;
      const apiData = payload?.[jobId] || payload?.[String(jobId)] || payload || {};
      dispatch(setFullJobTbdAuditItems(getTbdAuditItemsFromFullJob(apiData)));
      dispatch(setAllTbdForms(buildTbdFormsFromFullJob(apiData)));

      let sectionsChecked = 0;
      let sectionsUpdated = 0;
      let fieldsUpdated = 0;

      // Compare each section and only update changed fields
      Object.entries(sectionMap).forEach(([apiKey, sliceKey]) => {
        const apiSectionData = apiData[apiKey];
        if (!apiSectionData) {
          return; // No data from API for this section
        }

        const currentSectionData = currentJobDetails[sliceKey] || [];
        const apiArray = Array.isArray(apiSectionData) ? apiSectionData : [apiSectionData];
        
        sectionsChecked++;
        
        // Track which records have changes
        const updatedRecords = [];
        
        apiArray.forEach(apiRecord => {
          // Find matching record in Redux - match by Zone and ID if Zone exists, otherwise just ID
          let currentRecord;
          if (apiRecord.Zone !== undefined) {
            currentRecord = currentSectionData.find(
              record => record.Zone === apiRecord.Zone && record.ID === apiRecord.ID
            );
          } else {
            currentRecord = currentSectionData.find(
              record => record.ID === apiRecord.ID
            );
          }
          
          if (!currentRecord) {
            // New record not in Redux, add it
            updatedRecords.push(apiRecord);
            fieldsUpdated += Object.keys(apiRecord).length;
          } else {
            // Check for field-level changes
            const changedFields = {};
            let hasChanges = false;
            
            Object.keys(apiRecord).forEach(fieldKey => {
              // Deep comparison for nested objects/arrays, shallow for primitives
              if (JSON.stringify(currentRecord[fieldKey]) !== JSON.stringify(apiRecord[fieldKey])) {
                changedFields[fieldKey] = apiRecord[fieldKey];
                hasChanges = true;
                fieldsUpdated++;
              }
            });
            
            if (hasChanges) {
              // Include Zone and ID for targeting (Zone only if it exists)
              const updateRecord = apiRecord.Zone !== undefined
                ? { Zone: apiRecord.Zone, ID: apiRecord.ID, ...changedFields }
                : { ID: apiRecord.ID, ...changedFields };
              updatedRecords.push(updateRecord);
            }
          }
        });
        
        if (updatedRecords.length > 0) {
          sectionsUpdated++;
          dispatch(setMultipleJobDetails({ section: sliceKey, updates: updatedRecords }));
          dispatch(updateHasValues(updatedRecords));
          console.log(`syncJobData: Updated ${updatedRecords.length} records in ${sliceKey} section`);
        }
      });

      // Set all sections to 'success' after sync completes
      const successStatuses = {};
      Object.values(sectionMap).forEach(sliceKey => {
        successStatuses[sliceKey] = 'success';
      });
      dispatch(setAllApiStatus({ statuses: successStatuses }));

      const result = { 
        jobId, 
        sectionsChecked, 
        sectionsUpdated, 
        fieldsUpdated 
      };
      
      return result;
    } catch (err) {
      const message = err?.response?.status ? `${err.response.status}` : err?.message || "failed";
      console.error("syncJobData failed:", message, err);

      // Preserve existing section statuses on sync failure. The job remains viewable
      // with the last known good data, while the separate sync error state captures
      // the refresh failure.
      return rejectWithValue(message);
    }
  }
);

// Selectors
export const selectFullJobFetchLoading = (state) => state.fullJobFetch?.loading ?? false;
export const selectFullJobFetchSyncing = (state) => state.fullJobFetch?.syncing ?? false;
export const selectFullJobFetchError = (state) => state.fullJobFetch?.error ?? null;
export const selectFullJobFetchLastJobId = (state) => state.fullJobFetch?.lastJobId ?? null;

const fullJobFetchSlice = createSlice({
  name: "fullJobFetch",
  initialState: {
    loading: false,
    syncing: false,
    error: null,
    lastJobId: null,
    lastSyncResult: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFullJob.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFullJob.fulfilled, (state, action) => {
        state.freshSince = action.payload?.freshSince || 0;
        state.loading = false;
        state.lastJobId = action.payload?.jobId || null;
      })
      .addCase(fetchFullJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch full job";
      })
      .addCase(syncJobData.pending, (state) => {
        state.syncing = true;
        state.error = null;
      })
      .addCase(syncJobData.fulfilled, (state, action) => {
        state.syncing = false;
        state.lastSyncResult = action.payload;
      })
      .addCase(syncJobData.rejected, (state, action) => {
        state.syncing = false;
        state.error = action.payload || "Failed to sync job data";
      });
  },
});

export default fullJobFetchSlice.reducer;
